<template>
  <div class="amazon-right">
    <div v-if="workbench.amazon.loading" class="loading-placeholder">
      <div class="spinner"></div>
      <p>正在分析视觉元素并撰写 listing...</p>
    </div>

    <div v-else-if="workbench.amazon.result" class="copy-result">
      <div
        class="result-toolbar"
        style="
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-bottom: 16px;
        "
      >
        <button class="ghost-btn small copy-all-btn" @click="copyAllEnglish">
          一键复制全部英文
        </button>

        <button
          class="ghost-btn small"
          style="display: inline-flex; align-items: center; gap: 6px"
          @click="
            workbench.amazon.showTranslation = !workbench.amazon.showTranslation
          "
        >
          {{
            workbench.amazon.showTranslation ? "隐藏中文翻译" : "一键中英对照"
          }}
        </button>
      </div>

      <div class="copy-group">
        <div class="copy-label header-flex">
          <span>Product Title (标题)</span>
          <button
            class="mini-copy-btn"
            @click="copy(workbench.amazon.result.title)"
          >
            复制
          </button>
        </div>
        <div class="copy-content title">
          <div class="eng-text">{{ workbench.amazon.result.title }}</div>
          <div v-show="workbench.amazon.showTranslation" class="zh-text">
            {{ workbench.amazon.result.titleZh || "暂无翻译" }}
          </div>
        </div>
      </div>

      <div class="copy-group">
        <div class="copy-label header-flex">
          <span>Introduction (简介)</span>
          <button
            class="mini-copy-btn"
            @click="copy(workbench.amazon.result.introduction)"
          >
            复制
          </button>
        </div>
        <div class="copy-content">
          <div class="eng-text">{{ workbench.amazon.result.introduction }}</div>
          <div v-show="workbench.amazon.showTranslation" class="zh-text">
            {{ workbench.amazon.result.introductionZh || "暂无翻译" }}
          </div>
        </div>
      </div>

      <div class="copy-group">
        <div class="copy-label">Bullet Points (五点描述)</div>
        <ul class="bullet-list">
          <li
            v-for="(feature, index) in workbench.amazon.result.features"
            :key="index"
          >
            <div
              style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 12px;
              "
            >
              <div class="eng-text" style="flex: 1">{{ feature }}</div>
              <button
                class="mini-copy-btn"
                style="flex-shrink: 0; margin-top: 2px"
                @click="copy(feature)"
              >
                复制
              </button>
            </div>

            <div v-show="workbench.amazon.showTranslation" class="zh-text">
              {{ workbench.amazon.result.featuresZh?.[index] || "暂无翻译" }}
            </div>
          </li>
        </ul>
      </div>

      <div class="copy-group">
        <div class="copy-label header-flex">
          <div style="display: flex; align-items: center; gap: 10px">
            <span>Key Words (关键词)</span>
            <span style="font-size: 13px; color: #888; font-weight: normal">
              (字符数: {{ processedSearchTerms.length }})
            </span>
          </div>
          <button class="mini-copy-btn" @click="copy(processedSearchTerms)">
            复制
          </button>
        </div>
        <div class="copy-content tag-cloud" style="word-spacing: 4px">
          {{ processedSearchTerms }}
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      请上传图片并点击生成，AI 将为您自动撰写 Amazon Listing。
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useClipboard } from "@/composables/useClipboard";
import { useWorkbenchStore } from "@/stores/useWorkbenchStore";

const { copy } = useClipboard();
const workbench = useWorkbenchStore();

// 自动格式化流量词（彻底去重 + 去 s + 逗号改空格）
const processedSearchTerms = computed(() => {
  const keywords = workbench.amazon.result?.keywords || "";
  if (!keywords) return "";

  const cleanText = keywords.replace(/[,，]/g, " ").replace(/\b[sS]\b/g, "");
  const wordsArray = cleanText.split(/\s+/).filter(Boolean);
  const uniqueWords = [...new Set(wordsArray.map((w) => w.toLowerCase()))];

  return uniqueWords.join(" ");
});

// 一键拼接并复制所有英文内容
const copyAllEnglish = () => {
  const result = workbench.amazon.result;
  if (!result) return;

  // 格式化标题
  const titleText = `【Title】\n${result.title || ""}\n\n`;

  // 格式化简介大描述
  const introText = `【Product Description】\n${result.introduction || ""}\n\n`;

  // 格式化五点描述
  let bulletsText = `【Bullet Points】\n`;
  if (result.features && result.features.length) {
    result.features.forEach((feature, index) => {
      bulletsText += `${index + 1}. ${feature}\n`;
    });
  }
  bulletsText += `\n`;

  // 格式化搜索流量词
  const keywordsText = `【Search Terms】\n${processedSearchTerms.value}`;

  // 拼接全文并执行复制
  const fullContent = titleText + bulletsText + introText + keywordsText;
  copy(fullContent);
};
</script>

<style scoped>
/* 默认状态：原有的绿底绿字，加上平滑过渡效果 */
.copy-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #f0fdf4;
  color: #16a34a;
  border-color: #bbf7d0;
  transition: all 0.2s ease-in-out;
}

.copy-all-btn:hover {
  background-color: #dcfce7;
  border-color: #86efac;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(22, 163, 74, 0.1),
    0 2px 4px -1px rgba(22, 163, 74, 0.06);
}

.copy-all-btn:active {
  background-color: #bbf7d0;
  transform: translateY(1px);
  box-shadow: none;
}

/* --- 新增样式 --- */

/* 头部 Flex 布局，让 Label 和 复制按钮 左右两端对齐 */
.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 局部小复制按钮样式 */
.mini-copy-btn {
  cursor: pointer;
  background: transparent;
  border: 1px solid #d1d5db;
  color: #4b5563;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 1.5;
  transition: all 0.2s;
  outline: none;
}

.mini-copy-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #111827;
}

.mini-copy-btn:active {
  background: #e5e7eb;
}
</style>
